import React, { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { PencilSimple, UploadSimple, Trash, Check } from "@phosphor-icons/react";

/**
 * SignaturePad — captures a signature as a PNG data URL.
 * Modes: draw (canvas) OR upload (PNG/JPG). Emits onChange(dataUrl|null).
 */
export default function SignaturePad({ value, onChange, testid = "sig", disabled = false }) {
  const [mode, setMode] = useState("draw");
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef([0, 0]);
  const [dirty, setDirty] = useState(false);

  // (Re)init the canvas whenever we switch to draw mode.
  useEffect(() => {
    if (mode !== "draw") return;
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0f172a";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setDirty(false);
  }, [mode]);

  const relXY = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return [t.clientX - rect.left, t.clientY - rect.top];
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = relXY(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const [x, y] = relXY(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(last.current[0], last.current[1]);
    ctx.lineTo(x, y);
    ctx.stroke();
    last.current = [x, y];
    setDirty(true);
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setDirty(false);
  };

  const saveDrawn = () => {
    if (!dirty) return toast.error("Draw your signature first");
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onChange?.(dataUrl);
    toast.success("Signature captured");
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/image\/(png|jpe?g|webp)/i.test(file.type)) return toast.error("Upload a PNG, JPG, or WEBP image");
    if (file.size > 600 * 1024) return toast.error("Image too large — keep under 600 KB");
    const reader = new FileReader();
    reader.onload = () => { onChange?.(reader.result); toast.success("Signature uploaded"); };
    reader.readAsDataURL(file);
  };

  const removeSaved = () => onChange?.(null);

  return (
    <div className="border border-slate-200 bg-white rounded-sm p-4" data-testid={testid}>
      {value ? (
        <div className="flex items-center gap-4 flex-wrap">
          <div className="border border-slate-200 bg-slate-50 rounded-sm p-2 max-w-[240px]">
            <img src={value} alt="Signature" className="max-h-16 object-contain" data-testid={`${testid}-preview`}/>
          </div>
          <div>
            <div className="label-micro">Current signature</div>
            <div className="text-xs text-slate-500 mt-1">Used automatically when you Submit or Approve a proposal, and when you sign external documents.</div>
            <button type="button" onClick={removeSaved} disabled={disabled} data-testid={`${testid}-remove`}
                    className="mt-2 text-xs inline-flex items-center gap-1 border border-red-300 text-red-600 rounded-sm px-2 py-1 hover:bg-red-50 disabled:opacity-50">
              <Trash size={12}/> Remove
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="inline-flex text-[11px] font-semibold border border-slate-300 rounded-sm overflow-hidden">
            <button type="button" onClick={() => setMode("draw")} data-testid={`${testid}-tab-draw`}
                    className={`px-3 py-1 ${mode === "draw" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <PencilSimple size={11} className="inline mr-1"/> Draw
            </button>
            <button type="button" onClick={() => setMode("upload")} data-testid={`${testid}-tab-upload`}
                    className={`px-3 py-1 border-l border-slate-300 ${mode === "upload" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <UploadSimple size={11} className="inline mr-1"/> Upload PNG
            </button>
          </div>
          {mode === "draw" ? (
            <div className="mt-3">
              <canvas ref={canvasRef}
                      onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                      onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                      className="w-full h-40 border border-dashed border-slate-300 bg-white rounded-sm cursor-crosshair touch-none"
                      data-testid={`${testid}-canvas`}/>
              <div className="mt-2 flex gap-2">
                <button type="button" onClick={saveDrawn} disabled={disabled || !dirty} data-testid={`${testid}-save-draw`}
                        className="text-xs inline-flex items-center gap-1 bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-3 py-1.5 disabled:opacity-50 transition-colors">
                  <Check size={12}/> Save signature
                </button>
                <button type="button" onClick={clear} className="text-xs border border-slate-300 rounded-sm px-3 py-1.5 hover:bg-slate-100">Clear</button>
              </div>
            </div>
          ) : (
            <div className="mt-3">
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onUpload} disabled={disabled}
                     data-testid={`${testid}-upload-input`}
                     className="text-xs file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-slate-900 file:text-white file:rounded-sm hover:file:bg-blue-700 file:transition-colors"/>
              <div className="text-[11px] text-slate-500 mt-2">Best: transparent PNG, ~600×200 px, under 600 KB. A tight crop around the signature looks cleanest on documents.</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
